import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { BackLane, ElementSummary, NoteWaiting, RejectedEntry, SentBackEntry } from "../../../api/types";
import { OPEN_POINTS_PATH } from "../../../app/paths";
import { ReproposeDialog } from "../../../app/ProposeAgain";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { Counter } from "../../../ui/Counter";
import { EmptyState, Loading } from "../../../ui/EmptyState";
import { Pill } from "../../../ui/Pill";
import { SpeechMark } from "../../../ui/SpeechMark";
import { showToast } from "../../../ui/toastStore";
import { formatWhen } from "../shared/format";
import { TextArea } from "../shared/TextArea";
import { elementKindWord } from "../shared/words";

function ElementLine({ element }: { element: ElementSummary }) {
  return (
    <span className="ra-back__element">
      <strong>{element.name}</strong> <code>{element.code}</code> <span className="muted">{elementKindWord(element.kind)}</span>
    </span>
  );
}

function ElementLinks({ element }: { element: ElementSummary }) {
  const id = encodeURIComponent(element.id);
  return (
    <span className="row">
      <Link to={`/elements/${id}`}>Open the element</Link>
      <Link to={`/elements/${id}/edit`}>Fix it by hand</Link>
    </span>
  );
}

/** A rejected element is proposed again with new reasons, or withdrawn. It is not fixed by hand: the server refuses that. */
function RejectedRow({ entry }: { entry: RejectedEntry }) {
  const [proposing, setProposing] = useState(false);
  const id = encodeURIComponent(entry.element.id);
  return (
    <li className="ra-back__row" data-state="rejected">
      <div className="row">
        <Pill tone="rejected">Rejected</Pill>
        <ElementLine element={entry.element} />
      </div>
      <blockquote className="ra-quote">
        <p>{entry.reason}</p>
        <footer className="muted">The SME's reason.</footer>
      </blockquote>
      <span className="row">
        <Link to={`/elements/${id}`}>Open the element</Link>
        <Button variant="secondary" onClick={() => setProposing(true)} aria-label={`Propose again with new reasons: ${entry.element.name}`}>
          Propose again with new reasons
        </Button>
      </span>
      {proposing && <ReproposeDialog element={entry.element} onClose={() => setProposing(false)} />}
    </li>
  );
}

function SentBackRow({ entry }: { entry: SentBackEntry }) {
  return (
    <li className="ra-back__row" data-state="sent_back">
      <div className="row">
        <Pill tone="sent_back">{entry.line_id ? "Line returned" : "Sent back"}</Pill>
        <ElementLine element={entry.element} />
      </div>
      <blockquote className="ra-quote">
        <p>{entry.comment}</p>
        <footer className="muted">
          {entry.by.display}, {formatWhen(entry.at)}
          {entry.line_id ? ". About one line or value." : "."}
        </footer>
      </blockquote>
      <ElementLinks element={entry.element} />
    </li>
  );
}

function NoteRow({ item }: { item: NoteWaiting }) {
  const queryClient = useQueryClient();
  const answer = useApiCommand("add_note");
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const { note, element } = item;

  const send = async () => {
    setProblem(null);
    try {
      await answer.mutateAsync({ element_id: note.element_id, ...(note.line_id ? { line_id: note.line_id } : {}), kind: "engineer_note", text: text.trim(), answers_id: note.id });
      setText("");
      showToast({ text: "Your answer went to the SME as a note." });
      void queryClient.invalidateQueries({ queryKey: ["back_lane"] });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <li className="ra-back__row">
      <div className="row">
        <SpeechMark label="Note from the SME" />
        <ElementLine element={element} />
      </div>
      <blockquote className="ra-quote">
        <p>{note.text}</p>
        <footer className="muted">
          Note from the SME{note.line_id ? ", about one line or value" : ""}.{" "}
          {element.state === "validated"
            ? "The element is validated. Your answer is kept as a note."
            : "The element keeps waiting for the SME until you have answered and they have decided."}
        </footer>
      </blockquote>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <TextArea label="Your answer" value={text} onChange={(event) => setText(event.target.value)} rows={3} error={problem} />
        <div className="row">
          <Button type="submit" variant="primary" busy={answer.isPending} disabled={!text.trim()}>
            Send the answer
          </Button>
          <Link to={`/elements/${encodeURIComponent(element.id)}`}>Open the element</Link>
        </div>
      </form>
    </li>
  );
}

function Section({ id, title, count, empty, children }: { id: string; title: string; count: number; empty: string; children: ReactNode }) {
  return (
    <Card className="ra-section" aria-labelledby={id}>
      <h2 id={id}>
        {title} <span className="muted">({count})</span>
      </h2>
      {count === 0 ? <p className="muted">{empty}</p> : <ul className="ra-back__list">{children}</ul>}
    </Card>
  );
}

function Lane({ lane }: { lane: BackLane }) {
  const total = lane.sent_back.length + lane.notes_waiting.length + lane.rejected.length + lane.loaded_open_points.length;
  return (
    <>
      <Card className="ra-counts" aria-label="What came back">
        <div className="row">
          <Counter label="sent back" value={lane.sent_back.length} tone="sent_back" />
          <Counter label="notes to answer" value={lane.notes_waiting.length} />
          <Counter label="rejected" value={lane.rejected.length} tone="rejected" />
          <Counter label="open points" value={lane.loaded_open_points.length} />
        </div>
      </Card>

      {total === 0 && (
        <EmptyState title="Nothing came back from the SME">
          When the SME sends an element back, rejects one, or writes you a note, it shows here, and you hear about it as it happens.
        </EmptyState>
      )}

      <Section id="ra-back-sent" title="Sent back with a comment" count={lane.sent_back.length} empty="Nothing is sent back to you.">
        {lane.sent_back.map((entry) => (
          <SentBackRow key={`${entry.element.id}:${entry.line_id ?? ""}:${entry.at}`} entry={entry} />
        ))}
      </Section>

      <Section id="ra-back-notes" title="Notes waiting for your answer" count={lane.notes_waiting.length} empty="No note is waiting for an answer.">
        {lane.notes_waiting.map((item) => (
          <NoteRow key={item.note.id} item={item} />
        ))}
      </Section>

      <Section id="ra-back-rejected" title="Rejected" count={lane.rejected.length} empty="Nothing of yours is rejected.">
        {lane.rejected.map((entry) => (
          <RejectedRow key={entry.element.id} entry={entry} />
        ))}
      </Section>

      <Section id="ra-back-open" title="Open points for loaded content" count={lane.loaded_open_points.length} empty="No open point is waiting on loaded content.">
        {lane.loaded_open_points.map((point) => (
          <li key={point.id} className="ra-back__row">
            <div className="row">
              <Pill tone="loaded">Loaded content</Pill>
              <strong>{point.text}</strong>
            </div>
            <p className="muted">
              Raised by {point.raised_by}, {formatWhen(point.at)}. Loaded content is not reworked by the engineer alone; this is for the user to decide.
            </p>
          </li>
        ))}
      </Section>
    </>
  );
}

/** The engineer's lane for what comes back from the SME (ui-direction workflow 16): comments, notes, rejections and open points. */
export function BackPage() {
  const query = useApiQuery("back_lane", {});
  return (
    <div className="ra-page stack">
      <header className="ra-page__head">
        <h1>Back from the SME</h1>
        <p className="muted">What the SME sent back, noted or rejected. Work through it here, then hand it over again.</p>
        <p>
          <Link to={OPEN_POINTS_PATH}>Open points</Link> are questions that are still open: read and close them there.
        </p>
      </header>
      {query.isPending && <Loading>Reading what came back</Loading>}
      {query.isError && (
        <Card role="alert">
          <p className="form-error">{refusalText(query.error)}</p>
          <Button onClick={() => void query.refetch()}>Try again</Button>
        </Card>
      )}
      {query.data && <Lane lane={query.data} />}
    </div>
  );
}
